
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

import { LoaderService } from '../../Shared Utilities/global-loader/loader.service';
import { UserLoginService } from '../userlogin.service';

import {
  AuthenticationRequest,
  UserLoginAuthResponseDTO,
  UserLoginGenerateOtpRequestDTO,
  UserLoginGenerateOtpResponseDTO,
  UserLoginValidateOtpRequestDTO,
} from '../userlogin.interface';

@Component({
  selector: 'app-society-login',
  templateUrl: './society-login.component.html',
  styleUrls: ['./society-login.component.css']
})
export class SocietyLoginComponent implements OnInit, OnDestroy {

  loginForm!: FormGroup;
  mobileForm!: FormGroup;
  otpForm!: FormGroup;

  isMobileLogin = false;
  isOtpSent = false;
  maskedMessage = '';
  showPassword = false;

  timerSeconds = 0;
  readonly OTP_TIMEOUT_SECONDS = 60;
  private timerSub: Subscription | null = null;

  loading = false;
  resendOtpDisabled = true;

  private fb = inject(FormBuilder);
  private userLoginService = inject(UserLoginService);
  private router = inject(Router);
  private loader = inject(LoaderService);
  private toastr = inject(ToastrService);

  ngOnInit(): void {

    this.loginForm = this.fb.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      rememberMe: [false],
    });

    this.mobileForm = this.fb.group({
      mobileNo: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[6-9]\d{9}$/)
        ]
      ],
    });

    this.otpForm = this.fb.group({
      otp1: ['', Validators.required],
      otp2: ['', Validators.required],
      otp3: ['', Validators.required],
      otp4: ['', Validators.required],
      otp5: ['', Validators.required],
      otp6: ['', Validators.required],
      otp: [''],
    });

    if (this.userLoginService.isAuthenticated()) {
      this.router.navigate(['/']);
      return;
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  // ── Password Login ───────────────────────────────────────────

  onLogin(): void {
    this.loginForm.markAllAsTouched();

    if (this.loginForm.invalid) {
      return;
    }

    this.loader.show();

    const payload: AuthenticationRequest = {
      userName: this.loginForm.value.username,
      password: this.loginForm.value.password,
    };

    this.userLoginService.UserLogin_Auth(payload).subscribe({
      next: (res: UserLoginAuthResponseDTO) => {
        this.loader.dismiss();

        const token = res.data?.token?.trim();

        if (!token) {
          this.toastr.error(
            'Login response did not contain a token.'
          );
          return;
        }

        localStorage.setItem('access_token', token);

        // Save fresh user details after login.
        this.userLoginService.UserLogin_ValidateToken().subscribe({
          next: (userData) => {
            this.userLoginService.saveUserData(userData);

            if (res.statusCode === 403) {
              this.router.navigate([
                '/reset-password',
                res.data.userId
              ]);
            } else {
              this.router.navigate(['/']);
            }
          },
          error: () => {
            this.toastr.error(
              'Unable to load logged-in user details.'
            );
          }
        });
      },

      error: () => {
        this.loader.dismiss();

        this.toastr.error(
          'Login failed. Please check your credentials.'
        );
      },
    });
  }

  // ── OTP Login ────────────────────────────────────────────────

  sendOtp(): void {
    this.mobileForm.markAllAsTouched();

    if (this.mobileForm.invalid) {
      return;
    }

    this.loading = true;

    const payload: UserLoginGenerateOtpRequestDTO = {
      mobileNo: this.mobileForm.value.mobileNo,
    };

    this.userLoginService
      .UserLogin_LoginGenerateOtp(payload)
      .subscribe({
        next: (res: UserLoginGenerateOtpResponseDTO) => {
          this.loading = false;

          if (res.statusCode === 200) {
            this.isOtpSent = true;
            this.maskedMessage = res.message;

            this.startTimerFromExpiry(res.expiryTime);

            setTimeout(() => {
              this.resendOtpDisabled = false;
            }, 90000);

          } else {
            this.toastr.warning(res.message);
          }
        },

        error: () => {
          this.loading = false;

          this.toastr.error(
            'Failed to send OTP. Please try again.'
          );
        },
      });
  }

  verifyOtp(): void {
    this.otpForm.markAllAsTouched();

    if (this.otpForm.invalid) {
      return;
    }

    const payload: UserLoginValidateOtpRequestDTO = {
      mobileNo: this.mobileForm.value.mobileNo,
      otp: this.otpForm.value.otp,
    };

    this.loading = true;

    this.userLoginService
      .UserLogin_LoginValidateOtp(payload)
      .subscribe({
        next: (res: any) => {
          this.loading = false;

          if (res?.statusCode !== 200) {
            this.toastr.error(
              'Invalid OTP. Please try again.'
            );
            return;
          }

          const token = res.data?.token?.trim();

          if (!token) {
            this.toastr.error(
              'OTP verification succeeded but no token received.'
            );
            return;
          }

          localStorage.setItem('access_token', token);

          // Save fresh user details after OTP login.
          this.userLoginService.UserLogin_ValidateToken().subscribe({
            next: (userData) => {
              this.userLoginService.saveUserData(userData);
              this.router.navigate(['/']);
            },
            error: () => {
              this.toastr.error(
                'Unable to load logged-in user details.'
              );
            }
          });
        },

        error: () => {
          this.loading = false;

          this.toastr.error('OTP verification failed.');
        },
      });
  }

  resendOtp(): void {
    this.resendOtpDisabled = true;
    this.sendOtp();
  }

  changeMobileNo(): void {
    this.isOtpSent = false;
    this.maskedMessage = '';

    this.otpForm.reset();
    this.clearTimer();
  }

  toggleLoginMode(): void {
    this.isMobileLogin = !this.isMobileLogin;

    this.loginForm.reset();
    this.mobileForm.reset();
    this.otpForm.reset();

    this.isOtpSent = false;
    this.maskedMessage = '';
    this.resendOtpDisabled = true;

    this.clearTimer();
  }

  // ── OTP Input Helpers ────────────────────────────────────────

  onOtpInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value.length === 1 && index < 6) {
      document
        .getElementById('otp' + (index + 1))
        ?.focus();
    }

    this.syncOtpValue();
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      const input = event.target as HTMLInputElement;

      if (!input.value && index > 1) {
        document
          .getElementById('otp' + (index - 1))
          ?.focus();
      }
    }
  }

  private syncOtpValue(): void {
    let otp = '';

    for (let i = 1; i <= 6; i++) {
      otp += this.otpForm.get('otp' + i)?.value || '';
    }

    this.otpForm.patchValue(
      { otp },
      { emitEvent: false }
    );
  }

  // ── Timer ────────────────────────────────────────────────────

  startTimerFromExpiry(expiryTime: any): void {
    this.clearTimer();

    const diff = Math.floor(
      (new Date(expiryTime).getTime() - Date.now()) / 1000
    );

    this.timerSeconds = diff > 0 ? diff : 0;

    if (this.timerSeconds <= 0) {
      return;
    }

    this.timerSub = interval(1000)
      .pipe(takeWhile(() => this.timerSeconds > 0))
      .subscribe(() => {
        this.timerSeconds--;

        if (this.timerSeconds === 0) {
          this.clearTimer();
        }
      });
  }

  private clearTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = null;
    this.timerSeconds = 0;
  }
}
